import { datadogLogs } from "@datadog/browser-logs";
import { CognitoUser } from "amazon-cognito-identity-js";
import React, { useCallback, useEffect, useRef, useState } from "react";
import { FormattedMessage } from "react-intl";
import styled from "styled-components";

import { gql, WorkspaceWithLicense } from "@examspark/api";
import { hasTag, SystemTag } from "@examspark/common";
import {
  Button,
  Form,
  FormContext,
  inputGlow,
  Link,
  RouterLink,
  TextInput,
  Toolbar,
  VSpace,
} from "@examspark/common-ui";
import { User } from "@examspark/types";

import {
  AmplifyCognitoUser,
  currentAuthenticatedUser,
  signIn,
} from "../../auth";
import { AnalyticsEventName, useAnalytics } from "../../hooks/useAnalytics";
import { useLogout } from "../../hooks/useLogout";
import { useAppContext } from "../../providers/AppContext";
import AppLoading from "../AppLoading";

import LoginSelectWorkspace from "./LoginSelectWorkspace";

const Styled = styled.div`
  display: flex;
  flex-direction: column;

  select {
    ${inputGlow}
    appearance: none;
    background-color: #fff;
    border-radius: 0.3rem;
    border: solid 1px #ddd;
    box-sizing: border-box;
    font-family: inherit;
    font-size: inherit;
    line-height: 3.5rem;
    outline: none;
    padding: 0 1.5rem;
    width: 100%;
  }

  input {
    height: 3.5rem;
    font-size: 14px;
    padding: 1rem 1.5rem;
  }

  button {
    height: 3.5rem;
    font-size: 14px;
  }

  h1 {
    font-weight: 300;
    /* text-transform: uppercase; */
    color: #555;
    text-align: center;
  }
`;

interface Props {
  header?: React.ReactNode;
  onLogin: (data: CognitoUser) => void;
  allowRegistration?: boolean;
  onRegisterButtonClick?: () => void;
  onForgotPasswordButtonClick?: () => void;
  loginWithUrl?: string;
  loginWithText?: string;
  loginHelpUrl?: string;
}

const Login = (props: Props) => {
  const [context, dispatch] = useAppContext();
  const analytics = useAnalytics();
  const logout = useLogout();
  const [username, setUsername] = useState("");
  const [password, setPassword] = useState("");
  const [isLoggingIn, setIsLoggingIn] = useState(false);
  const [needsAuthentication, setNeedsAuthentication] = useState(false);
  const [user, setUser] = useState<User>();
  const [cognitoUser, setCognitoUser] = useState<CognitoUser>();
  const [workspaces, setWorkspaces] = useState<WorkspaceWithLicense[]>();
  const [selectedWorkspace, setSelectedWorkspace] =
    useState<WorkspaceWithLicense>();
  const ref = useRef<HTMLInputElement>(null);

  const { onRegisterButtonClick } = props;

  const handleLogin = async () => {
    try {
      const normalizedUsername = username.trim().toLowerCase();
      if (normalizedUsername.length === 0 || password.length === 0) {
        throw new Error("Please enter a username and password.");
      }

      setIsLoggingIn(true);
      datadogLogs.setGlobalContextProperty("session", {
        user: {
          email: normalizedUsername,
        },
      });
      const signInResult = await signIn(normalizedUsername, password);
      if (!signInResult) {
        throw new Error("An unexpected error occurred during signin.");
      }

      await processSignInResult(signInResult);
    } catch (error) {
      alert(error);
      setIsLoggingIn(false);
    }
  };

  const processSignInResult = useCallback(
    async (signInResult: AmplifyCognitoUser) => {
      setIsLoggingIn(true);

      const user = await gql.operations.user.getUser(
        signInResult.attributes.sub,
      );

      if (!user) {
        logout();
        throw new Error("User could not be found.");
      }

      const userId = signInResult.attributes.sub;
      setCognitoUser(signInResult);
      setUser(user);

      // query user workspaces, determine if any of them are authorized to use
      // bonfire.
      const userWorkspaces = await gql.operations.user.listUserWorkspaces({
        userId: userId,
        appId: context.app?.id,
        primaryOnly: true,
      });
      const filteredWorkspaces = userWorkspaces?.filter(
        // @todo do we need this? doesn't primaryOnly do it?
        (w) => !hasTag(w, SystemTag.InheritedFrom),
      );
      if (!filteredWorkspaces?.length) {
        if (!props.allowRegistration || onRegisterButtonClick == null) {
          throw new Error(
            "You do not belong to any workspaces that are authorized to use this application.",
          );
        }
        onRegisterButtonClick();
        return;
      }

      setIsLoggingIn(false);
      if (filteredWorkspaces.length === 1) {
        setSelectedWorkspace(filteredWorkspaces[0]);
        return;
      }

      if (context.workspace?.id != null) {
        const contextWorkspace = filteredWorkspaces.find(
          (w) => w.id === context.workspace?.id,
        );
        if (contextWorkspace != null) {
          //
          // We already have a workspace set, probably by LTI/SSO launch
          // so just respect that
          //
          setSelectedWorkspace(contextWorkspace);
        }
      }

      setWorkspaces(filteredWorkspaces);
    },
    [
      context.app?.id,
      onRegisterButtonClick,
      props.allowRegistration,
      logout,
      context.workspace?.id,
    ],
  );

  const completeSignIn = () => {
    if (selectedWorkspace == null) return;
    if (cognitoUser == null) return;
    if (user == null) {
      console.error("User is not defined");
      return;
    }

    try {
      dispatch({
        type: "SET_USER",
        data: user,
      });
      dispatch({
        type: "SELECT_WORKSPACE_WITH_LICENSE",
        data: selectedWorkspace,
      });
      props.onLogin(cognitoUser);
      analytics.identify(user, { workspaceId: selectedWorkspace.id });
      analytics.trackEvent(AnalyticsEventName.Login);
    } catch (e) {
      console.error(e);
      alert(e);
    }
  };

  useEffect(() => {
    completeSignIn();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [selectedWorkspace, user, cognitoUser]);

  useEffect(() => {
    currentAuthenticatedUser().then((user) => {
      if (user == null || context.user?.id == null) {
        setNeedsAuthentication(true);
        return;
      }
      setUsername(user.attributes.email);
      processSignInResult(user);
    });
  }, [context.user?.id, processSignInResult]);

  return (
    <Styled>
      {props.header}
      {workspaces === undefined &&
        (!needsAuthentication ? (
          <AppLoading text={`Logging in ${username}`} />
        ) : (
          <FormContext.Provider value={{ disabled: isLoggingIn }}>
            <Form>
              <Form.FieldList>
                <Form.Field label="Username">
                  <TextInput
                    ref={ref}
                    autoCapitalize="off"
                    autoFocus
                    type="email"
                    name="username"
                    value={username}
                    onChange={(e) => setUsername(e.target.value)}
                    onEnterKeyPress={handleLogin}
                  />
                </Form.Field>
                <Form.Field label="Password">
                  <TextInput
                    type="password"
                    name="password"
                    value={password}
                    onChange={(e) => setPassword(e.target.value)}
                    onEnterKeyPress={handleLogin}
                  />
                </Form.Field>
                <VSpace style={{ marginTop: "2rem" }}>
                  <Toolbar>
                    {props.allowRegistration && (
                      <Button
                        data-cy="login-register"
                        title="Register"
                        variant="contained"
                        stretch
                        onClick={props.onRegisterButtonClick}
                      >
                        Register
                      </Button>
                    )}
                    <Button
                      data-cy="login-submit"
                      title="Login"
                      color="primary"
                      variant="contained"
                      stretch
                      onClick={(e) => {
                        e.preventDefault();
                        handleLogin();
                      }}
                      isLoading={isLoggingIn}
                      disabled={
                        username.length === 0 || password.length === 0
                          ? true
                          : undefined
                      }
                    >
                      <FormattedMessage
                        id="login"
                        defaultMessage="Login"
                        values={{ ts: Date.now() }}
                      />
                    </Button>
                  </Toolbar>
                  <Toolbar>
                    <RouterLink to="/login/reset">Forgot Password</RouterLink>
                    {props.loginHelpUrl && (
                      <Toolbar.Right>
                        <Link href={props.loginHelpUrl}>Help</Link>
                      </Toolbar.Right>
                    )}
                  </Toolbar>
                  {props.loginWithUrl && (
                    <Toolbar>
                      <Link href={props.loginWithUrl} target="_blank">
                        {props.loginWithText}
                      </Link>
                    </Toolbar>
                  )}
                </VSpace>
              </Form.FieldList>
            </Form>
          </FormContext.Provider>
        ))}
      {workspaces !== undefined && (
        <>
          <VSpace style={{ gap: "2rem" }}>
            <LoginSelectWorkspace
              workspaces={workspaces}
              onSelect={setSelectedWorkspace}
            />
            <Button
              title="Go Back"
              variant="contained"
              stretch
              onClick={(e) => {
                logout().then(() => {
                  setWorkspaces(undefined);
                });
              }}
              isLoading={isLoggingIn}
            >
              Go Back
            </Button>
          </VSpace>
        </>
      )}

      <form onSubmit={handleLogin}></form>
    </Styled>
  );
};

export default Login;
